import { LinkButton, Text, Tooltip } from '@etg/wings';
import Media from 'react-media';
import { css, mediaQueries } from '@eti/styles';
import { price as priceUtil } from '@eti/utils';
import type { AgeType, TaxCategory } from '@eti/schema-types';
import { ScreenReaderPrice } from '@eti/components';
import { CartRow, Price } from '../../CartComponents';
import { getTaxesAndFeesTotalPriceCarrierSpecific } from '../utils';

const tooltipContentColumnGapStyles = css`
  margin-top: 4px;
`;

type CarrierPriceBreakdown = {
  carrier: {
    name: string;
  };
  taxesAndFees: TaxCategory[];
  vat: {
    value: number;
  };
};

export interface CartTaxesAndFeesCarrierPriceBreakdownTypes {
  ageType: AgeType;
  carrierPriceBreakdowns: CarrierPriceBreakdown[];
  pricePerAgeTypeLabel: string;
  taxesAndFees: TaxCategory[];
  taxesAndFeesLabel: string;
  totalVat: number;
  travelerIndex: number;
}

const CartTaxesAndFeesCarrierPriceBreakdown = ({
  ageType,
  carrierPriceBreakdowns = [],
  pricePerAgeTypeLabel,
  taxesAndFees,
  taxesAndFeesLabel,
  totalVat,
  travelerIndex,
}: CartTaxesAndFeesCarrierPriceBreakdownTypes) => (
  <CartRow data-testid={`cart-taxes-fees-carrier-price-breakdown-traveler-${travelerIndex}`}>
    {carrierPriceBreakdowns.length > 1 ? (
      <Tooltip
        content={
          <>
            <Text>{pricePerAgeTypeLabel}</Text>
            {carrierPriceBreakdowns.map(
              ({ carrier, taxesAndFees: taxesAndFeesPerCarrier = [], vat }, index: number) => (
                <CartRow
                  className={tooltipContentColumnGapStyles}
                  key={`taxes-fees-carrier-price-breakdown-${carrier?.name}-${index}`}
                >
                  <Media query={`${mediaQueries.small.only}`}>
                    {(isMobile) =>
                      isMobile ? (
                        <Text>{carrier?.name ?? ''}</Text>
                      ) : (
                        <Text>{`${taxesAndFeesLabel} ${carrier?.name ?? ''}`}</Text>
                      )
                    }
                  </Media>
                  <Price aria-hidden>
                    {priceUtil.formatPrice(
                      getTaxesAndFeesTotalPriceCarrierSpecific(
                        taxesAndFeesPerCarrier ?? [],
                        vat?.value,
                      ),
                    )}
                  </Price>
                  <ScreenReaderPrice
                    price={priceUtil.formatPrice(
                      getTaxesAndFeesTotalPriceCarrierSpecific(
                        taxesAndFeesPerCarrier ?? [],
                        vat?.value,
                      ),
                    )}
                  />
                </CartRow>
              ),
            )}
          </>
        }
        data-testid={`cart-taxesAndFees-tooltip-traveler-${travelerIndex}`}
        title={taxesAndFeesLabel}
        trigger="click"
      >
        <LinkButton data-testid={`cart-taxesAndFees-traveler-${travelerIndex}-link-button`}>
          {taxesAndFeesLabel}
        </LinkButton>
      </Tooltip>
    ) : (
      <Text>{`${taxesAndFeesLabel} ${carrierPriceBreakdowns[0]?.carrier?.name ?? ''}`}</Text>
    )}
    <Price aria-hidden data-testid={`cart-travelers-${ageType}-vat`}>
      {priceUtil.formatPrice(getTaxesAndFeesTotalPriceCarrierSpecific(taxesAndFees, totalVat))}
    </Price>
    <ScreenReaderPrice
      price={priceUtil.formatPrice(
        getTaxesAndFeesTotalPriceCarrierSpecific(taxesAndFees, totalVat),
      )}
    />
  </CartRow>
);

export default CartTaxesAndFeesCarrierPriceBreakdown;
